/// <reference types="dom-chromium-ai" />
import { Button } from 'antd'
import { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

type TranslatorAvailability =
  | null
  | 'unsupported'
  // These below states are from https://developer.mozilla.org/en-US/docs/Web/API/Translator/availability_static#return_value
  | 'unavailable'
  | 'downloadable'
  | 'downloading'
  | 'available'

export function TranslationTextForTooltip({
  cleanedText,
  projectLocale
}: {
  cleanedText: string
  projectLocale: string | null
}) {
  const nativeLanguage = 'en'

  const [translatedText, setTranslatedText] = useState<string | null>(null)
  const [translatorAvailability, setTranslatorAvailability] =
    useState<TranslatorAvailability>(null)

  const [sourceLanguage, requiresTranslation] = useMemo(() => {
    if (!projectLocale) {
      // NOTE: There appears to be a timing issue with Project not always being loaded before rendering, hence nullable locale
      return ['', false]
    }

    const projectLanguage = projectLocale.split('-')[0]
    return [projectLanguage, projectLanguage !== nativeLanguage]
  }, [projectLocale])

  const options: TranslatorCreateCoreOptions = useMemo(() => {
    return {
      sourceLanguage: sourceLanguage,
      targetLanguage: nativeLanguage
    }
  }, [sourceLanguage, nativeLanguage])

  useEffect(() => {
    if (!requiresTranslation) {
      return
    }

    if (translatorAvailability !== null) {
      return
    }

    if (!Translator) {
      setTranslatorAvailability('unsupported')
      return
    }

    async function checkTranslatorAvailability() {
      const availability = await Translator.availability(options)
      setTranslatorAvailability(availability)
    }

    void checkTranslatorAvailability()
  }, [translatorAvailability, options, requiresTranslation])

  const onInitClicked = async () => {
    console.log('Initializing Translator for', options)
    if (navigator.userActivation.isActive === false) {
      throw new Error(
        'Translator initialization requires a user gesture to proceed.'
      )
    }
    setTranslatorAvailability('downloading') // Force status to downloading
    const translator = await Translator.create(options)
    console.debug('Translator initialized:', translator)
    translator.destroy()
    setTranslatorAvailability(null) // Trigger re-check of availability
  }

  useEffect(() => {
    async function translateText() {
      if (translatorAvailability === 'available') {
        const translator = await Translator.create(options)

        const totalInputQuota = translator.inputQuota
        const inputUsage = await translator.measureInputUsage(cleanedText)
        console.debug('Translation quota:', totalInputQuota, inputUsage)

        if (inputUsage > totalInputQuota) {
          setTranslatedText(
            `* Translation API quota exceeded [${inputUsage}/${totalInputQuota}] *`
          )
          console.warn(
            'Translation API quota exceeded:',
            inputUsage,
            totalInputQuota
          )
        } else {
          const translation = await translator.translate(cleanedText)
          setTranslatedText(translation)
        }
        translator.destroy()
      }
    }

    if (requiresTranslation) {
      void translateText()
    }
  }, [
    translatorAvailability,
    cleanedText,
    requiresTranslation,
    sourceLanguage,
    options
  ])

  if (!requiresTranslation) {
    return <div>{cleanedText}</div>
  }

  return (
    <div>
      <div>{cleanedText}</div>
      <TranslationContainer>
        <OCRTranslationDisplay
          translatorAvailability={translatorAvailability}
          sourceLanguage={sourceLanguage}
          nativeLanguage={nativeLanguage}
          translatedText={translatedText}
          onPermitClicked={onInitClicked}
        />
      </TranslationContainer>
    </div>
  )
}

function OCRTranslationDisplay({
  translatorAvailability,
  sourceLanguage,
  nativeLanguage,
  translatedText,
  onPermitClicked
}: {
  translatorAvailability: TranslatorAvailability
  sourceLanguage: string
  nativeLanguage: string
  translatedText: string | null
  onPermitClicked: () => Promise<void>
}) {
  const translationType = useMemo(() => {
    return `${sourceLanguage} → ${nativeLanguage}`
  }, [sourceLanguage, nativeLanguage])

  switch (translatorAvailability) {
    case null:
      return null // Initial state, but avoid too much flicker
    case 'unsupported':
      return <div>Translation API not supported in this browser.</div>
    case 'unavailable':
      return (
        <div>
          Translations for '{translationType}' not available in this browser.
        </div>
      )
    case 'downloadable':
      return (
        <>
          <div>Translations for '{translationType}' need permission.</div>
          <div>
            <Button
              type="primary"
              onClick={e => {
                void onPermitClicked()
                e.stopPropagation()
              }}
            >
              Permit
            </Button>
          </div>
        </>
      )
    case 'downloading':
      return <div>Translations for '{translationType}' are downloading...</div>
    case 'available':
      if (translatedText === null) {
        return <div>Translating text...</div>
      } else {
        return (
          <div>
            Translation ({translationType}):
            <br />
            <span style={{ fontStyle: 'italic' }}>{translatedText}</span>
          </div>
        )
      }
  }
}

const TranslationContainer = styled.div`
  margin-top: 10px;
  padding-top: 2px;
  border-top: 1px solid ${colours.BORDER_GREY};
`
